import uniqid from 'uniqid';
import {getConfigData} from '../middleware';

export const PRESIGN_TIMEOUT_MS = 20000;
export const UPLOAD_TIMEOUT_MS = 120000;

const MAX_BASE_NAME_LENGTH = 60;
const EXT_BY_TYPE = {'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png'};

/**
 * Builds a unique, URL/S3-safe object name for a picked file. Several pickers
 * (e.g. iOS Safari) name every library photo "image.jpg", so the raw name can't
 * be used as the S3 object name or the files overwrite each other.
 */
export const buildUploadName = (file) => {
  const rawName = file.name || '';
  const dot = rawName.lastIndexOf('.');
  const rawBase = dot > 0 ? rawName.slice(0, dot) : rawName;
  const rawExt = dot > 0 ? rawName.slice(dot + 1) : '';

  const base =
    rawBase
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // "é" -> "e" instead of "_"
      .replace(/[^A-Za-z0-9_-]+/g, '_')
      .replace(/_resized/gi, '') // reserved by the resizer Lambda
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, MAX_BASE_NAME_LENGTH) || 'photo';
  const ext =
    rawExt.replace(/[^A-Za-z0-9]/g, '').toLowerCase() ||
    EXT_BY_TYPE[file.type] ||
    'jpg';

  return `${uniqid()}-${base}.${ext}`;
};

export const getPresignedUrl = async (fileName, photosKey, signal) => {
  const config = getConfigData()[getConfigData().env].tempUploads;
  const url = `${config.schema}://${config.host}/${config.context}/${
    config.path
  }?fileName=${encodeURIComponent(fileName)}&key=${photosKey}`;

  const response = await fetch(url, {signal});
  if (!response.ok) {
    throw new Error(`Presign request failed with status ${response.status}`);
  }
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get presigned URL');
  }

  return {
    presignedUrl: data.fileUploadURL,
    s3Key: data.s3Key,
  };
};

/**
 * PUTs the file to the presigned URL. Always settles: success, HTTP error,
 * network error, timeout and abort all reject/resolve, so a stalled request
 * can never block the upload queue. `signal` aborts the request.
 */
export const uploadToS3WithProgress = (
  file,
  presignedUrl,
  onProgress,
  signal,
  timeoutMs = UPLOAD_TIMEOUT_MS
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.timeout = timeoutMs;

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(
          new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`)
        );
      }
    });
    xhr.addEventListener('error', () =>
      reject(new Error(`Upload error: ${xhr.statusText || 'network error'}`))
    );
    xhr.addEventListener('timeout', () =>
      reject(new Error(`Upload timed out after ${timeoutMs}ms`))
    );
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    if (signal) {
      if (signal.aborted) {
        reject(new Error('Upload aborted'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), {once: true});
    }

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/** Presign + upload with a bounded timeout and a single retry. */
export const uploadFile = async (
  file,
  uploadName,
  photosKey,
  {onStatus, onProgress, signal, attempts = 2} = {}
) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (signal && signal.aborted) {
      throw new Error('Upload aborted');
    }
    try {
      if (onStatus) onStatus('getting-url');
      const presignController = new AbortController();
      const abortPresign = () => presignController.abort();
      if (signal) signal.addEventListener('abort', abortPresign, {once: true});
      const presignTimer = setTimeout(abortPresign, PRESIGN_TIMEOUT_MS);
      let presigned;
      try {
        presigned = await getPresignedUrl(
          uploadName,
          photosKey,
          presignController.signal
        );
      } finally {
        clearTimeout(presignTimer);
        if (signal) signal.removeEventListener('abort', abortPresign);
      }

      if (onStatus) onStatus('uploading');
      await uploadToS3WithProgress(
        file,
        presigned.presignedUrl,
        onProgress,
        signal
      );
      return presigned;
    } catch (error) {
      lastError = error;
      if (signal && signal.aborted) break;
    }
  }
  throw lastError;
};
